"use client";

import useItem from "hooks/useItem";
import useList from "hooks/useList";
import AlertsContext from "providers/AlertProvider";
import { useContext, useEffect, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IconNames } from "components/Icon/Icon";
import useRouter from "common/hooks/use-router";
import { Sidebar } from "primereact/sidebar";
import clsx from "clsx";
import { ListBulletIcon, MapIcon } from "@heroicons/react/24/outline";
import CrudView from "./components/CrudView";
import type { CrudHeaderAction } from "./components/CrudHeader";
import type { TableColumn } from "../DataTable/DataTable";
import type { CrudFilter } from "./components/CrudFilters";
import SelectionActionBar from "../DataTable/components/SelectionActionBar";

type ViewMode = "table" | "map";

type FormMode = "page" | "sidebar" | "modal";

type Props = {
  collectionName: string;
  columns?: TableColumn[];
  headerActions?: CrudHeaderAction[];
  filters?: CrudFilter[];
  defaultFilters?: any;
  defaultSort?: string;
  populate?: string | object;
  canEdit?: boolean;
  canDelete?: boolean;
  canSeeDetails?: boolean;
  icon?: IconNames;
  onEdit?: (id: any) => void;
  onDelete?: (id: any) => void;
  onNavigate?: (id: any) => void;
  formMode?: FormMode;
  SidebarFormComponent?: (props: any) => JSX.Element;
  sidebarPosition?: "left" | "right" | "top" | "bottom";
  sidebarClassName?: string;
  actionsHeader?: string;
  className?: string;
  withMapView?: boolean;
  getMarkerPosition?: (item: any) => { lat: number; lng: number } | null;
  getMarkerLabel?: (item: any) => string;
  refreshTrigger?: number;
};

function CrudList({
  collectionName,
  columns = [],
  headerActions = [],
  filters = [],
  defaultFilters,
  defaultSort,
  populate,
  canEdit = true,
  canDelete = true,
  canSeeDetails = true,
  icon = "UserIcon",
  onEdit,
  onDelete,
  onNavigate,
  formMode = "page",
  SidebarFormComponent,
  sidebarPosition = "right",
  sidebarClassName = "w-full md:w-2/3 lg:w-1/2",
  actionsHeader,
  className,
  withMapView = false,
  getMarkerPosition,
  getMarkerLabel,
  refreshTrigger,
}: Props) {
  const { t } = useTranslation();
  const { setAlert } = useContext(AlertsContext);
  const { navigate } = useRouter();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [itemIdToEdit, setItemIdToEdit] = useState<number | null>(null);
  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [isDeletingMultiple, setIsDeletingMultiple] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [tableItemsPerPage] = useState(25);

  const openSidebar = useCallback((id: number | null) => {
    setItemIdToEdit(id);
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
    setItemIdToEdit(null);
  }, []);

  const {
    fetchItems,
    items,
    isFetching,
    filters: activeFilters,
    setFilter,
    sort,
    setSort,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    setItemsPerPage,
    error,
  } = useList<any>(collectionName, {
    defaultSort: defaultSort ?? "createdAt:DESC",
    defaultFilters: defaultFilters ?? {},
    cachePrefix: collectionName,
    populate,
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  useEffect(() => {
    if (!withMapView) return;
    setItemsPerPage(viewMode === "map" ? 1000 : tableItemsPerPage);
  }, [viewMode, withMapView, tableItemsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (refreshTrigger) fetchItems();
  }, [refreshTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  const { removeItem } = useItem<any>(collectionName, "");

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, "danger");
        return e;
      }
      onDelete?.(id);
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert, onDelete]
  );

  const handleBulkDelete = useCallback(async () => {
    if (selectedItems.length === 0) return;

    setIsDeletingMultiple(true);
    try {
      // Delete all selected items in parallel
      await Promise.all(
        selectedItems.map(async (item) => {
          const { id } = item;
          removeListItem(id);
          await removeItem(id);
          onDelete?.(id);
        })
      );

      setAlert(
        t("actions.deleteMultipleSuccess", { count: selectedItems.length }),
        "success"
      );
      setSelectedItems([]);
      fetchItems();
    } catch (e: any) {
      setAlert(e, "danger");
    } finally {
      setIsDeletingMultiple(false);
    }
  }, [
    selectedItems,
    removeListItem,
    removeItem,
    onDelete,
    fetchItems,
    setAlert,
    t,
  ]);

  const handleEdit = useCallback(
    (id: any) => {
      if (onEdit) {
        onEdit(id);
      } else if (formMode === "sidebar") {
        openSidebar(id);
      } else {
        navigate(`/${collectionName}/edit/${id}`);
      }
    },
    [onEdit, formMode, openSidebar, navigate, collectionName]
  );

  const translatedHeaderActions = headerActions.map((action) => {
    const { action: actionType } = action;
    if (typeof actionType === "string") {
      if (actionType === "openSidebar") {
        return {
          ...action,
          title: t(action.title),
          action: () => openSidebar(null),
        };
      }
    }

    return action;
  });

  return (
    <div
      className={clsx(
        "mx-auto max-w-full relative flex-1 h-full text-slate-500 overflow-y-hidden",
        className
      )}
    >
      <div
        className="h-[500px] lg:h-[250px] w-full absolute top-0 left-0 flex bg-cover bg-center"
        style={{ backgroundImage: "url(/images/header-background.png)" }}
      />
      <div className="flex-1 px-4">
        <CrudView
          viewToggle={
            withMapView ? (
              <div className="inline-flex rounded-lg border border-gray-200 bg-white/90 p-1 gap-1">
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                    viewMode === "table"
                      ? "bg-primary text-white"
                      : "text-slate-500 hover:bg-gray-100"
                  )}
                >
                  <ListBulletIcon className="w-4 h-4" />
                  Liste
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("map")}
                  className={clsx(
                    "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
                    viewMode === "map"
                      ? "bg-primary text-white"
                      : "text-slate-500 hover:bg-gray-100"
                  )}
                >
                  <MapIcon className="w-4 h-4" />
                  Carte
                </button>
              </div>
            ) : undefined
          }
          title={t(`${collectionName}.listTitle`)}
          subtitle={t(`${collectionName}.listSubtitle`)}
          icon={icon}
          headerActions={translatedHeaderActions}
          data={items ?? []}
          columns={columns}
          isFetching={isFetching}
          pageCount={pageCount}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          itemsCount={itemsCount}
          itemsPerPage={itemsPerPage}
          sort={sort}
          setSort={setSort}
          onDelete={canDelete ? handleDelete : undefined}
          onEdit={canEdit ? handleEdit : undefined}
          onRowClick={
            canSeeDetails
              ? onNavigate || handleEdit
              : canEdit
              ? handleEdit
              : undefined
          }
          filters={filters}
          activeFilters={activeFilters}
          setFilter={setFilter}
          emptyMessage={t(`${collectionName}.noItems`)}
          actionsHeader={actionsHeader}
          colorMode="light"
          selectionMode="checkbox"
          selection={selectedItems}
          onSelectionChange={setSelectedItems}
          mode={viewMode}
          getMarkerPosition={getMarkerPosition}
          getMarkerLabel={getMarkerLabel}
        />
        <SelectionActionBar
          selectedCount={selectedItems.length}
          onDelete={canDelete ? handleBulkDelete : undefined}
          onClearSelection={() => setSelectedItems([])}
          isDeleting={isDeletingMultiple}
        />
      </div>
      {formMode && SidebarFormComponent && (
        <Sidebar
          visible={isSidebarOpen}
          onHide={closeSidebar}
          position={sidebarPosition}
          className={sidebarClassName}
        >
          <SidebarFormComponent
            id={itemIdToEdit}
            onBack={closeSidebar}
            onSubmitSuccess={fetchItems}
          />
        </Sidebar>
      )}
    </div>
  );
}

export default CrudList;
